import Feature from 'ol/Feature';
import LineString from 'ol/geom/LineString';
import { Coordinate } from 'ol/coordinate';

/**
 * A reversible edit within a GPX edit session. Commands mutate session state
 * only — nothing hits the API until the session is saved.
 */
export interface ICommand {
    readonly label: string;
    do(): void;
    undo(): void;
}

/**
 * Undo/redo stack for an edit session. isDirty is derived from the undo depth:
 * undoing everything returns the session to its baseline.
 */
export class CommandStack {

    private undoStack: ICommand[] = [];
    private redoStack: ICommand[] = [];

    /** Fired after any execute/undo/redo/clear so the toolbar can refresh. */
    public onChange: (() => void) | null = null;

    /**
     * Pushes a command. Pass alreadyApplied=true when the change has physically
     * happened (e.g. an OL Modify drag mutated the geometry) and only the
     * inverse needs recording.
     */
    public execute(command: ICommand, alreadyApplied: boolean = false): void {
        if (!alreadyApplied) {
            command.do();
        }
        this.undoStack.push(command);
        this.redoStack = [];
        this.onChange?.();
    }

    public undo(): void {
        const command = this.undoStack.pop();
        if (!command) {
            return;
        }
        command.undo();
        this.redoStack.push(command);
        this.onChange?.();
    }

    public redo(): void {
        const command = this.redoStack.pop();
        if (!command) {
            return;
        }
        command.do();
        this.undoStack.push(command);
        this.onChange?.();
    }

    get canUndo(): boolean {
        return this.undoStack.length > 0;
    }

    get canRedo(): boolean {
        return this.redoStack.length > 0;
    }

    get isDirty(): boolean {
        return this.undoStack.length > 0;
    }

    public clear(): void {
        this.undoStack = [];
        this.redoStack = [];
        this.onChange?.();
    }
}

// ---------------------------------------------------------------------------
// Geometry commands — swap a segment feature's coordinates (EPSG:3857)
// ---------------------------------------------------------------------------

export class GeometryCommand implements ICommand {

    public constructor(
        public readonly label: string,
        private feature: Feature,
        private before: Coordinate[],
        private after: Coordinate[],
        private onGeometryChanged: () => void
    ) {
    }

    public do(): void {
        (<LineString>this.feature.getGeometry()).setCoordinates(this.after);
        this.onGeometryChanged();
    }

    public undo(): void {
        (<LineString>this.feature.getGeometry()).setCoordinates(this.before);
        this.onGeometryChanged();
    }
}

/**
 * A Modify drag may touch several segment features at once — one command
 * captures the before/after of all of them.
 */
export class MultiGeometryCommand implements ICommand {

    public constructor(
        public readonly label: string,
        private changes: Array<{ feature: Feature; before: Coordinate[]; after: Coordinate[] }>,
        private onGeometryChanged: () => void
    ) {
    }

    public do(): void {
        for (const change of this.changes) {
            (<LineString>change.feature.getGeometry()).setCoordinates(change.after);
        }
        this.onGeometryChanged();
    }

    public undo(): void {
        for (const change of this.changes) {
            (<LineString>change.feature.getGeometry()).setCoordinates(change.before);
        }
        this.onGeometryChanged();
    }
}

// ---------------------------------------------------------------------------
// Waypoint commands — operate on the session's draft collection
// ---------------------------------------------------------------------------

export interface IWaypointDraft {
    /** waypointGuid for existing waypoints, clientRef for new ones. */
    key: string;
    waypointGuid: string | null;
    clientRef: string | null;
    name: string;
    description: string | null;
    typeId: number;
    lonLat: [number, number];
}

export interface IWaypointDraftHost {
    addDraft(draft: IWaypointDraft): void;
    removeDraft(key: string): void;
    applyDraftFields(key: string, fields: { name: string; description: string | null; typeId: number }): void;
    setDraftPosition(key: string, lonLat: [number, number]): void;
}

export class AddWaypointCommand implements ICommand {
    public readonly label = 'Add waypoint';

    public constructor(private host: IWaypointDraftHost, private draft: IWaypointDraft) {
    }

    public do(): void {
        this.host.addDraft(this.draft);
    }

    public undo(): void {
        this.host.removeDraft(this.draft.key);
    }
}

export class DeleteWaypointCommand implements ICommand {
    public readonly label = 'Delete waypoint';

    public constructor(private host: IWaypointDraftHost, private draft: IWaypointDraft) {
    }

    public do(): void {
        this.host.removeDraft(this.draft.key);
    }

    public undo(): void {
        this.host.addDraft(this.draft);
    }
}

export class UpdateWaypointCommand implements ICommand {
    public readonly label = 'Edit waypoint';

    public constructor(
        private host: IWaypointDraftHost,
        private key: string,
        private before: { name: string; description: string | null; typeId: number },
        private after: { name: string; description: string | null; typeId: number }
    ) {
    }

    public do(): void {
        this.host.applyDraftFields(this.key, this.after);
    }

    public undo(): void {
        this.host.applyDraftFields(this.key, this.before);
    }
}

export class MoveWaypointCommand implements ICommand {
    public readonly label = 'Move waypoint';

    public constructor(
        private host: IWaypointDraftHost,
        private key: string,
        private before: [number, number],
        private after: [number, number]
    ) {
    }

    public do(): void {
        this.host.setDraftPosition(this.key, this.after);
    }

    public undo(): void {
        this.host.setDraftPosition(this.key, this.before);
    }
}

// ---------------------------------------------------------------------------
// Metadata command
// ---------------------------------------------------------------------------

export interface IRouteMetadata {
    displayName: string;
    notes: string | null;
    colourHex: string;
    tags: string[];
}

export class UpdateMetadataCommand implements ICommand {
    public readonly label = 'Edit route details';

    public constructor(
        private apply: (metadata: IRouteMetadata) => void,
        private before: IRouteMetadata,
        private after: IRouteMetadata
    ) {
    }

    public do(): void {
        this.apply(this.after);
    }

    public undo(): void {
        this.apply(this.before);
    }
}
